import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { filter, take } from 'rxjs/operators';
import { FIELD_TYPES } from '../../../../shared/fields/field-types';
import { STANDARD_FIELDS } from '../../../../shared/fields/standard-fields';
import * as fromRoot from '../../core/store';
import * as assetActions from '../../core/store/assets/asset.actions';
import * as layoutActions from '../../core/store/layout/layout.actions';
import { Asset, Field, ProjectCurrency } from '../../core/store/models';
import { ActionStatus, SavingMessage, Severity, TableTypes } from '../../core/store/types';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import { enabledFieldTypes, FormPosition } from '../../shared/components/dual-input/dual-input.component';
import { PDropdownOption } from '../../shared/interfaces/p-dropdown-options';

export interface MergeAssetsModalData {
    fields: Field[];
    tableType: TableTypes;
    combineAsset: any[];
}

@Component({
    templateUrl: './merge-assets-modal.component.html',
    styleUrls: ['./merge-assets-modal.component.scss'],
    standalone: false
})
export class MergeAssetsModalComponent implements OnInit {
    allFields: Field[] = [];
    assetCurrency: ProjectCurrency;
    assetForm: UntypedFormGroup;
    /** List of things that have to be the same, are merged from both, or aren't real and shouldn't be shown */
    excludedFields: string[] = [
        STANDARD_FIELDS.franchise.canonical_name,
        STANDARD_FIELDS.project.canonical_name,
        STANDARD_FIELDS.department.canonical_name,
        STANDARD_FIELDS.sub_department.canonical_name,
        STANDARD_FIELDS.division.canonical_name,
        STANDARD_FIELDS.warehouse.canonical_name,
        STANDARD_FIELDS.subLocation.canonical_name,
        STANDARD_FIELDS.warehouseSubLocation.canonical_name,
        STANDARD_FIELDS.photos.canonical_name,
        STANDARD_FIELDS.group_ids.canonical_name,
        STANDARD_FIELDS.created_at.canonical_name,
        STANDARD_FIELDS.updated_at.canonical_name,
        'other_cost',
        'local_cost',
        STANDARD_FIELDS.sub_code.canonical_name,
        STANDARD_FIELDS.qty_on_hand.canonical_name,
        STANDARD_FIELDS.free_field_1.canonical_name,
        STANDARD_FIELDS.free_field_2.canonical_name,
        STANDARD_FIELDS.free_field_3.canonical_name,
        STANDARD_FIELDS.created_by_user.canonical_name,
        STANDARD_FIELDS.assetEvents.canonical_name

    ];
    fieldsWithDifferentValues: Field[] = [];
    fieldTypes = FIELD_TYPES;
    formPosition = FormPosition;
    moment = moment;
    saving: boolean;
    savingMessage?: SavingMessage[] = [];
    selectedControls: { [canonicalName: string]: AbstractControl } = {};
    showAllFields = false;
    combineAsset = this.data.combineAsset;

    constructor(@Inject(MAT_DIALOG_DATA) private data: MergeAssetsModalData, private store: Store<fromRoot.State>, public dialogRef: MatDialogRef<MergeAssetsModalComponent>, public fb: UntypedFormBuilder, public dialog: MatDialog) { }

    /**
     * Setup initial state: get the project currency of the primary asset, check for differences
     * between assets, setup allFields, and build the form
     */
    ngOnInit() {
        this.allFields = this.processFields();

        this.diffCheckAssets();

        this.buildForm(this.fieldsWithDifferentValues);
    }

    /**
     * Builds the formGroup depending on available fields and patches any
     * reciprocal values from the given assets
     */
    buildForm(fields: Field[]): { formObj: { [key: string]: any }, patchObj: { [key: string]: any } } {
        const formObj = {};
        const patchObj = {};

        fields.forEach(field => {
            for (let i = 0; i < this.combineAsset.length; i++) {
                const value = this.getValue(field, i);
                if (field && !this.excludedFields.includes(field.canonical_name)) {
                    formObj[`${field.canonical_name}${i}`] = [''];
                    if (!this.selectedControls[field.canonical_name] && this.assetForm && i === 0) {
                        this.selectedControls[field.canonical_name] = this.assetForm.get(`${field.canonical_name}${i}`);
                    }
                    if (field.type === FIELD_TYPES.DATE.type) {
                        patchObj[`${field.canonical_name}${i}`] = new Date(moment.utc(value).local().format());
                    } else if (field.type === FIELD_TYPES.PICKLIST.type) {
                        patchObj[`${field.canonical_name}${i}`] = this.makePicklistOption(field, i).value;
                    } else if (field.type === FIELD_TYPES.CURRENCY.type || field.type === FIELD_TYPES.CHECKBOX.type) {
                        patchObj[`${field.canonical_name}${i}`] = field.getValueFromAsset(this.combineAsset[i]);
                    } else {
                        patchObj[`${field.canonical_name}${i}`] = value;
                    }
                }
            }
        });

        this.assetForm = this.fb.group(formObj);
        this.assetForm.patchValue(patchObj);

        return { formObj, patchObj };
    }

    processFields() {
        return this.data.fields
            .map(field => {
                // can generalize this for renaming other fields
                if (field.canonical_name === STANDARD_FIELDS.cost.canonical_name) {
                    return new Field({ ...field, name: 'Origin Cost' });
                } else if (field.canonical_name === STANDARD_FIELDS.location.canonical_name) {
                    const subLocationField = this.data.fields.find(f => f.canonical_name === STANDARD_FIELDS.subLocation.canonical_name);
                    return new Field({ ...field, name: `${field.name} - ${subLocationField.name}` });
                } else if (field.canonical_name === STANDARD_FIELDS.warehouseLocation.canonical_name) {
                    const warehouseSubLocation = this.data.fields.find(f => f.canonical_name === STANDARD_FIELDS.warehouseSubLocation.canonical_name);
                    return new Field({ ...field, name: `${field.name} - ${warehouseSubLocation.name}` });
                }
                return field;
            })
            .filter(field => !this.excludedFields.includes(field.canonical_name))
            .sort((a, b) => a.sort_order - b.sort_order);
    }

    /**
     * Gets field's edited value if any or asset's original value
     */
    getValue(field: Field, formPosition: number) {
        const asset = this.combineAsset[formPosition];
        const originalValue = asset.displayFields[field.canonical_name] ? asset.displayFields[field.canonical_name] : '';
        if (enabledFieldTypes.includes(field.type)) {
            const editedValue = this.assetForm ? this.assetForm.value[`${field.canonical_name}${formPosition}`] : null;
            return editedValue ? editedValue : originalValue;
        }
        return originalValue;
    }

    /**
     * Checks for differences in the given assets' displayFields, which is
     * used to setup fieldsWithDifferentValues for the 'Show Less Fields' feature
     */
    diffCheckAssets(): void {
        const fieldValue = [];
        this.allFields.forEach(field => {
            for (let i = 0; i < this.combineAsset.length - 1; i++) {
                let oldValue = this.combineAsset[i];
                let newValue = this.combineAsset[i + 1];
                const primaryValue = field.getValueFromAsset(oldValue);
                const secondaryValue = field.getValueFromAsset(newValue);
                if (!this.excludedFields.includes(field.canonical_name) && (primaryValue || secondaryValue)) {
                    if (field.type === FIELD_TYPES.DATE.type) {
                        if (!moment(primaryValue, 'DD MMM. YYYY').isSame(moment(secondaryValue, 'DD MMM. YYYY'))) {
                            fieldValue.push(field);
                        }
                    }
                    else if (primaryValue !== secondaryValue) {
                        fieldValue.push(field);
                    }
                }
            }
        })
        const filterFieldvalues = fieldValue.filter((tag, index, array) => array.findIndex(t => t.canonical_name == tag.canonical_name) == index);
        this.fieldsWithDifferentValues.push(...filterFieldvalues);
    }

    /**
     * Checks if the field for the particular canonical name is the Picklist type
     */
    isPicklistField(field: Field): boolean {
        return field.type === FIELD_TYPES.PICKLIST.type;
    }

    /**
     * Makes a single picklist option for a particular field and position in the form.
     * Is mainly used to pre-fill picklists in the form with values from the assets
     */
    makePicklistOption(field: Field, position: FormPosition): PDropdownOption {
        const asset = this.combineAsset[position];
        if (field && asset) {
            return { label: asset.displayFields[field.canonical_name], value: asset[field.canonical_name] };
        }

        return null;
    }

    /**
     * Gets values from the selected form controls and opens the confirm modal.
     * The values should be sent up to the merge assets endpoint.
     */
    onMergeAssets() {
        const formValues = {};
        let mergedAsset: Asset;
        Object.keys(this.selectedControls).forEach(canonicalName => {
            const control: AbstractControl = this.selectedControls[canonicalName];
            if (control) {
                formValues[canonicalName] = this.selectedControls[canonicalName].value;
                for (let i = 0; i < this.combineAsset.length; i++) {

                    // Set sub loc/warehouse sub loc to selected loc/warehouose loc's value
                    if (canonicalName === STANDARD_FIELDS.location.canonical_name) {
                        let subLocationId;
                        if (this.combineAsset[0].location_id === this.selectedControls[canonicalName].value) {
                            subLocationId = this.combineAsset[0].sub_location_id;
                        } else {
                            subLocationId = this.combineAsset[i].sub_location_id;
                        }
                        formValues[STANDARD_FIELDS.subLocation.canonical_name] = subLocationId;
                    } else if (canonicalName === STANDARD_FIELDS.warehouseLocation.canonical_name) {
                        let warehouseSubLocationId;
                        if (this.combineAsset[0].warehouse_location_id === this.selectedControls[canonicalName].value) {
                            warehouseSubLocationId = this.combineAsset[0].warehouse_sub_location_id;
                        } else {
                            warehouseSubLocationId = this.combineAsset[i].warehouse_sub_location_id;
                        }
                        formValues[STANDARD_FIELDS.subLocation.canonical_name] = warehouseSubLocationId;
                    }
                }
            }

        });
        this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirm Merge',
                message: 'Are you sure you want to merge these assets? This cannot be undone.'
            },
            autoFocus: false
        }).afterClosed().pipe(take(1)).subscribe(confirm => {
            let primaryAsset: Asset;
            if (this.selectedControls['sync_id']) {
                const syncId = this.selectedControls['sync_id'].value;
                const assetId = parseInt(syncId.match(/\d+/)[0], 10);
                primaryAsset = this.combineAsset.find(asset => asset.id === assetId);
            } else {
                primaryAsset = this.combineAsset[0];
            }
            mergedAsset = new Asset({ ...primaryAsset, ...formValues });
            const deleteAssets = []
            this.combineAsset.forEach(value => {
                if (value.id !== primaryAsset.id) {
                    deleteAssets.push(value.id);
                }
            })
            if (confirm) {
                if (!this.showAllFields) {
                    if (!this.fieldsWithDifferentValues.find(f => f.canonical_name === STANDARD_FIELDS.cost.canonical_name)) {
                        delete mergedAsset.cost; // If cost is not being updated, we don't want to pass the potential asset's calculated cost in another currency
                    }
                }
                this.store.dispatch(new assetActions.MergeAssetsAction({ asset: mergedAsset, assetIds: deleteAssets, tableType: this.data.tableType }));
                this.saving = true;
                this.savingMessage = [{ severity: Severity.Info, summary: 'Saving...' }];
                this.store.select(fromRoot.getAssetSaving).pipe(filter(saving => saving !== ActionStatus.Loading), take(1)).subscribe(saving => {
                    if (saving === ActionStatus.Complete) {
                        const message = 'Assets have been merged.';
                        this.store.dispatch(new layoutActions.InfoAction({ message }));
                        this.dialogRef.close({
                            success: true,
                            mergedAsset: mergedAsset,
                            confirmedByUser: confirm  // Pass the confirmation result from second dialog
                        });
                    } else {
                        this.dialogRef.close({ success: false });
                    }
                    this.saving = false;
                    this.savingMessage = [];
                });
            }
        });

        return mergedAsset;
    }

    /**
     * Used as an EventEmitter for the DualInputComponents in the form, this is how we will
     * know which form controls' values to send up to the endpoint during submission
     */
    onSelectControl(controlObj: { [canonicalName: string]: AbstractControl }) {
        this.selectedControls = { ...this.selectedControls, ...controlObj };
    }

    /**
     * Rebuilds the form using allFields
     */
    onShowAllFields() {
        this.buildForm(this.allFields);
        this.showAllFields = true;
        return;
    }

    /**
     * Rebuilds the form using fieldsWithDifferentValues
     */
    onShowLessFields() {
        this.buildForm(this.fieldsWithDifferentValues);
        this.showAllFields = false;
        return;
    }
}
